import sensors from "@/utils/sensors/sensors_config";

/**
 * 前端性能监控模块
 * 收集 Web Vitals 等性能数据
 *
 * ⚠️ 临时功能（未来会迁移到 Sentry）
 */

// ============ 配置区域 ============
// 🔧 有效期：超过此日期自动关闭（格式：YYYY-MM-DD）
const EXPIRE_DATE = "2026-02-28";

// 🔧 采样率：1.0 表示全量上报
const SAMPLE_RATE = 1.0;

// 🔧 是否开启（总开关）
const ENABLED = true;

// 🔧 是否开启 JS 错误监控（默认关闭）
const ENABLE_JS_ERROR = true;

// 🔧 是否开启长任务监控
const ENABLE_LONG_TASKS = true;

// 🔧 是否开启慢资源上报
const ENABLE_SLOW_RESOURCES = true;
// ============ 配置区域结束 ============

// 检查是否在有效期内
const isWithinExpireDate = () => {
  const now = new Date();
  const expire = new Date(EXPIRE_DATE + "T23:59:59");
  return now <= expire;
};

// 采样判断
const shouldSample = (() => {
  const key = "__perf_sample__";
  let sampled = sessionStorage.getItem(key);
  if (sampled === null) {
    sampled = Math.random() < SAMPLE_RATE ? "1" : "0";
    try {
      sessionStorage.setItem(key, sampled);
    } catch {
      // ignore
    }
  }
  return sampled === "1";
})();

// 检查是否启用
const isEnabled = () => {
  if (!ENABLED) return false;
  if (!isWithinExpireDate()) {
    // console.log(`[Performance Monitor] 已过期（${EXPIRE_DATE}），自动关闭`);
    return false;
  }
  if (!shouldSample) return false;
  return true;
};

interface PerformanceMetrics {
  // Web Vitals
  lcp?: number;
  fid?: number;
  cls?: number;
  fcp?: number;
  ttfb?: number;
  inp?: number;

  // 页面加载性能
  dom_ready?: number;
  load_time?: number;
  dom_interactive?: number;
  dns_time?: number;
  tcp_time?: number;
  ssl_time?: number;
  request_time?: number;
  response_time?: number;
  dom_parse_time?: number;
  resource_load_time?: number;

  // 页面信息
  page_url?: string;
  page_name?: string;

  // 设备与网络
  device_memory?: number;
  hardware_concurrency?: number;
  connection_type?: string;
  effective_type?: string;
  downlink?: number;
  rtt?: number;
}

const metrics: PerformanceMetrics = {};
let clsValue = 0;

// 安全执行
const safeExecute = (fn: () => void) => {
  try {
    fn();
  } catch (error) {
    console.warn("[Performance Monitor] Error:", error);
  }
};

// 获取网络信息
const getNetworkInfo = () => {
  const nav = navigator as any;
  const connection = nav.connection || nav.mozConnection || nav.webkitConnection;
  if (connection) {
    return {
      connection_type: connection.type,
      effective_type: connection.effectiveType,
      downlink: connection.downlink,
      rtt: connection.rtt,
    };
  }
  return {};
};

// 获取设备信息
const getDeviceInfo = () => {
  const nav = navigator as any;
  return {
    device_memory: nav.deviceMemory,
    hardware_concurrency: nav.hardwareConcurrency,
  };
};

// 收集导航性能数据
const collectNavigationTiming = () => {
  const entries = performance.getEntriesByType("navigation") as PerformanceNavigationTiming[];
  if (entries.length > 0) {
    const timing = entries[0];
    metrics.ttfb = timing.responseStart - timing.requestStart;
    metrics.dns_time = timing.domainLookupEnd - timing.domainLookupStart;
    metrics.tcp_time = timing.connectEnd - timing.connectStart;
    metrics.ssl_time =
      timing.secureConnectionStart > 0 ? timing.connectEnd - timing.secureConnectionStart : 0;
    metrics.request_time = timing.responseStart - timing.requestStart;
    metrics.response_time = timing.responseEnd - timing.responseStart;
    metrics.dom_interactive = timing.domInteractive - timing.fetchStart;
    metrics.dom_parse_time = timing.domContentLoadedEventEnd - timing.domInteractive;
    metrics.dom_ready = timing.domContentLoadedEventEnd - timing.fetchStart;
    metrics.load_time = timing.loadEventEnd - timing.fetchStart;
    metrics.resource_load_time = timing.loadEventEnd - timing.domContentLoadedEventEnd;
  }
};

// 创建 PerformanceObserver
const createObserver = (type: string, callback: (entries: PerformanceEntryList) => void) => {
  try {
    const observer = new PerformanceObserver((entryList) => {
      safeExecute(() => callback(entryList.getEntries()));
    });
    observer.observe({ type, buffered: true });
    return observer;
  } catch {
    return null;
  }
};

const observeLCP = () =>
  createObserver("largest-contentful-paint", (entries) => {
    const lastEntry = entries[entries.length - 1] as any;
    if (lastEntry) metrics.lcp = lastEntry.startTime;
  });

const observeFID = () =>
  createObserver("first-input", (entries) => {
    const firstEntry = entries[0] as any;
    if (firstEntry) metrics.fid = firstEntry.processingStart - firstEntry.startTime;
  });

const observeCLS = () =>
  createObserver("layout-shift", (entries) => {
    for (const entry of entries as any[]) {
      if (!entry.hadRecentInput) clsValue += entry.value;
    }
    metrics.cls = clsValue;
  });

const observeFCP = () =>
  createObserver("paint", (entries) => {
    for (const entry of entries) {
      if (entry.name === "first-contentful-paint") metrics.fcp = entry.startTime;
    }
  });

const observeINP = () => {
  let maxDuration = 0;
  return createObserver("event", (entries) => {
    for (const entry of entries as any[]) {
      if (entry.duration > maxDuration) {
        maxDuration = entry.duration;
        metrics.inp = entry.duration;
      }
    }
  });
};

// 上报性能数据
const reportPerformance = (pageName?: string) => {
  safeExecute(() => {
    const data: PerformanceMetrics = {
      ...metrics,
      ...getNetworkInfo(),
      ...getDeviceInfo(),
      page_url: window.location.href,
      page_name: pageName || document.title,
    };

    const cleanData: Record<string, any> = {};
    for (const [key, value] of Object.entries(data)) {
      if (value !== undefined && !Number.isNaN(value)) {
        cleanData[key] = typeof value === "number" ? Math.round(value * 100) / 100 : value;
      }
    }

    sensors.track("WebPerformance", cleanData);
    console.log("[Performance Monitor] Reported:", cleanData);
  });
};

// 监控长任务
const observeLongTasks = (pageName?: string) =>
  createObserver("longtask", (entries) => {
    for (const entry of entries) {
      if (entry.duration > 100) {
        sensors.track("LongTask", {
          page_url: window.location.href,
          page_name: pageName || document.title,
          duration: Math.round(entry.duration),
          start_time: Math.round(entry.startTime),
        });
      }
    }
  });

// 上报慢资源
const reportSlowResources = (pageName?: string) => {
  safeExecute(() => {
    const resources = performance.getEntriesByType("resource") as PerformanceResourceTiming[];
    const slowResources = resources
      .filter((r) => r.duration > 1000)
      .slice(0, 5)
      .map((r) => ({
        name: r.name.substring(0, 200),
        type: r.initiatorType,
        duration: Math.round(r.duration),
        size: r.transferSize || 0,
      }));

    if (slowResources.length > 0) {
      sensors.track("SlowResource", {
        page_url: window.location.href,
        page_name: pageName || document.title,
        slow_resources: JSON.stringify(slowResources),
        slow_resource_count: slowResources.length,
      });
    }
  });
};

// 上报 JS 错误
const setupErrorReporting = (pageName?: string) => {
  window.addEventListener("error", (event) => {
    safeExecute(() => {
      sensors.track("JSError", {
        page_url: window.location.href,
        page_name: pageName || document.title,
        error_message: event.message?.substring(0, 500),
        error_filename: event.filename?.substring(0, 200),
        error_lineno: event.lineno,
        error_colno: event.colno,
      });
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    safeExecute(() => {
      sensors.track("UnhandledRejection", {
        page_url: window.location.href,
        page_name: pageName || document.title,
        reason: String(event.reason)?.substring(0, 500),
      });
    });
  });
};

let observers: (PerformanceObserver | null)[] = [];

export default {
  /**
   * 初始化性能监控
   * @param pageName 页面名称（可选）
   */
  $initPerformance(pageName?: string) {
    if (!isEnabled()) return;

    // 启动性能观察器
    observers = [observeLCP(), observeFID(), observeCLS(), observeFCP(), observeINP()];

    if (ENABLE_LONG_TASKS) {
      observers.push(observeLongTasks(pageName));
    }

    if (ENABLE_JS_ERROR) {
      setupErrorReporting(pageName);
    }

    // 页面加载完成后收集导航性能
    if (document.readyState === "complete") {
      collectNavigationTiming();
    } else {
      window.addEventListener("load", () => {
        setTimeout(collectNavigationTiming, 0);
      });
    }

    // 延迟 1 秒上报
    setTimeout(() => {
      reportPerformance(pageName);
      if (ENABLE_SLOW_RESOURCES) {
        reportSlowResources(pageName);
      }
    }, 1000);
  },

  /**
   * 清理性能监控
   */
  $cleanupPerformance() {
    safeExecute(() => {
      for (const observer of observers) {
        observer?.disconnect();
      }
      observers = [];
    });
  },
};
